import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { sanitizeInstruction, sanitizeFoodOrName } from './textSanitizer';

// Register Noto Sans Gujarati font
Font.register({
  family: 'Noto Sans Gujarati',
  fonts: [
    { src: '/fonts/Noto_Sans_Gujarati/static/NotoSansGujarati-Regular.ttf', fontWeight: 'normal' },
    { src: '/fonts/Noto_Sans_Gujarati/static/NotoSansGujarati-Bold.ttf', fontWeight: 'bold' }
  ]
});

// Register Noto Sans Devanagari font
Font.register({
  family: 'Noto Sans Devanagari',
  fonts: [
    { src: '/fonts/Noto_Sans_Devanagari/static/NotoSansDevanagari-Regular.ttf', fontWeight: 'normal' },
    { src: '/fonts/Noto_Sans_Devanagari/static/NotoSansDevanagari-Bold.ttf', fontWeight: 'bold' }
  ]
});

// Prevent breaking syllables/ligatures across Indic scripts and all languages
const noBreakHyphenation = (word) => [word];
Font.registerHyphenationCallback(noBreakHyphenation);

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingLeft: 50,
    paddingRight: 50,
    paddingBottom: 30,
    paddingTop: 30
  },
  reportHeaderContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginBottom: 15,
    borderBottom: '1px solid #000',
    paddingBottom: 10,
  },
  doctorInfo: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  doctorName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  clinicName: {
    fontSize: 12,
    color: '#555',
  },
  reportDate: {
    fontSize: 12,
    color: '#555',
    textAlign: 'right',
  },
  sensitivityLine: {
    fontSize: 12,
    marginBottom: 20,
    color: '#ff0000',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  imageGrid: {
    flexDirection: 'column',
    marginBottom: 20,
    width: '100%',
  },
  imageRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
    width: '100%',
  },
  imageContainer: {
    width: '45%',
    alignItems: 'center',
  },
  singleImageContainer: {
    width: '90%',
    alignItems: 'center',
    margin: '0 auto',
  },
  singleAllergyImage: {
    width: '100%',
    height: 400,
    objectFit: 'contain',
    marginBottom: 5,
    border: '1px solid #eee',
  },
  allergyImage: {
    width: '100%',
    height: 150,
    objectFit: 'contain',
    marginBottom: 5,
    border: '1px solid #eee',
  },
  allergyName: {
    fontSize: 11,
    fontWeight: 700,
    color: '#0f4c75',
    textAlign: 'left',
    // fontFamily will be set dynamically
  },
  section: {
    margin: 0,
    padding: 0,
    flexGrow: 1
  },
  subHeader: {
    fontSize: 12,
    marginBottom: 8,
    fontWeight: 'bold',
    marginTop: 10,
    color: '#ff0000',
    fontFamily: 'Noto Sans Gujarati', // Keep Gujarati for section headers
  },
  listItem: {
    fontSize: 11,
    marginBottom: 4,
    // fontFamily will be set dynamically
  }
});

// Dynamic font selection for allergy names and instructions
const getFontFamily = (lang) => {
  if (lang === 'gujarati') return 'Noto Sans Gujarati';
  if (lang === 'hindi' || lang === 'marathi') return 'Noto Sans Devanagari';
  return undefined; // default
};

// Create Document Component
const InstructionReport = ({ data }) => {
  const selectedLanguage = data.selectedLanguage || 'english';

  const getAllergyNameEnglish = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    if (obj.english && typeof obj.english === 'string' && obj.english.trim()) return obj.english;
    const first = Object.values(obj).find(v => typeof v === 'string' && v.trim());
    return first || '';
  };

  const getInstructionInSelectedLanguage = (obj) => {
    if (!obj) return '';
    let val = '';
    if (typeof obj === 'string') {
      val = selectedLanguage === 'english' ? obj : '';
    } else if (selectedLanguage === 'english') {
      val = (obj.english && typeof obj.english === 'string') ? obj.english : '';
    } else if (obj[selectedLanguage] && typeof obj[selectedLanguage] === 'string') {
      val = obj[selectedLanguage];
    }
    const content = (val || '').replace(/[.,;:!?।॥\s\-_/\\()[\]{}'"`~@#$%^&*+=<>|]/g, '');
    if (!content || val.trim().toLowerCase() === 'not provided') {
      return '';
    }
    return val;
  };

  // Indic languages (Hindi, Marathi, Gujarati) need slightly more line height and breathing room to prevent glyph clipping
  const isIndic = selectedLanguage === 'hindi' || selectedLanguage === 'marathi' || selectedLanguage === 'gujarati';

  console.log('InstructionReport data:', data); // Debug the incoming data
  console.log('Selected Language:', selectedLanguage);
  console.log('All Instructions:', data?.allInstructions);
  console.log('Food Names:', data?.foodNames);
  console.log('Allergy Images:', data?.allergyImages);

  const allergyImages = (data?.allergyImages || []).map(img => ({
    ...img,
    name: getAllergyNameEnglish(img.name) // Allergy name is ALWAYS displayed in English only
  }));
  const isSingleImage = allergyImages.length === 1;

  const imageRows = [];
  if (!isSingleImage) {
    for (let i = 0; i < allergyImages.length; i += 2) {
      imageRows.push(allergyImages.slice(i, i + 2));
    }
  }

  // Instructions strictly in selected language (no English fallback for non-English)
  const rawInstructions = data?.allInstructions || data?.instructions || [];
  const instructions = rawInstructions
    .map(inst => getInstructionInSelectedLanguage(inst))
    .map(text => sanitizeInstruction(text, selectedLanguage))
    .filter(inst => inst && typeof inst === 'string' && inst.trim() !== '');

  // Food allergen names are displayed in selected language
  const rawFoods = data?.foodNames || data?.foods || [];
  const foodNames = rawFoods
    .map(food => getInstructionInSelectedLanguage(food))
    .map(text => sanitizeFoodOrName(text))
    .filter(food => food && typeof food === 'string' && food.trim() !== '');

  // Chunk food names into rows of 3 for robust multi-page pagination
  const foodRows = [];
  for (let i = 0; i < foodNames.length; i += 3) {
    foodRows.push(foodNames.slice(i, i + 3));
  }

  // Format report date
  const reportDate = data?.createdAt ? new Date(data.createdAt).toLocaleDateString('en-GB') : '';

  // Custom header component matching prescription PDF (Small/A5 only, fixed overlap)
  const PDFHeader = (
    <View style={{ flexDirection: 'column', width: '100%', marginBottom: 10 }}>
      {/* Top Row: Logos and Doctor Info */}
      <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: -10, marginBottom: 0, width: '100%', minHeight: 90 }}>
        {/* Left Logo */}
        <View style={{ width: 85, paddingBottom: 0 }}>
          <Image src={'/image_logo.jpg'} style={{ width: '100%', objectFit: 'contain' }} />
        </View>

        {/* Dr Eshita V Shah */}
        <View style={{ flexDirection: 'column', alignItems: 'flex-start', flex: 1.4, paddingLeft: 15 }}>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontWeight: 800, fontSize: 12, letterSpacing: 0.5, marginBottom: 3 }}>DR. ESHITA V. SHAH</Text>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 8, marginBottom: 1 }}>M.D (Pulmonary Medicine)</Text>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 8, marginBottom: 1 }}>European Diploma in Respiratory Medicine</Text>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 8, marginBottom: 1 }}>Fellowship - Interventional Pulmonology</Text>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 8 }}>& Thoracic Oncology (New Delhi)</Text>
        </View>

        {/* Dr Vipul Shah */}
        <View style={{ flexDirection: 'column', alignItems: 'flex-end', flex: 1.6, paddingRight: 15 }}>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontWeight: 800, fontSize: 12, letterSpacing: 0.5, marginBottom: 3 }}>DR. VIPUL SHAH</Text>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 8, marginBottom: 1 }}>PhD (Allergy), Diploma in Allergy,</Text>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 8, marginBottom: 1 }}>Asthma & Immunology (USA)</Text>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 8, marginBottom: 1 }}>Member of British Society for Allergy</Text>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 8 }}>& Clinical Immunology</Text>
        </View>

        {/* Right Logo */}
        <View style={{ width: 60, paddingBottom: 0 }}>
          <Image src={'/image_chest.jpg'} style={{ width: '100%', objectFit: 'contain' }} />
        </View>
      </View>
      {/* Divider */}
      <View style={{ height: 2, backgroundColor: '#8B0000', marginTop: 5, marginBottom: 6, width: '100%' }} />
      {/* Date */}
      {reportDate && (
        <View style={{ textAlign: 'right', width: '100%' }}>
          <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 9, marginTop: -3, paddingLeft: 0, paddingRight: 12 }}><Text hyphenationCallback={noBreakHyphenation} style={{ fontWeight: 'bold' }}>Date:</Text> {reportDate}</Text>
        </View>
      )}
    </View>
  );

  // Helper to safely render text words without mid-word breaks across any environment
  const renderTextWords = (text) => {
    if (!text || typeof text !== 'string') return '';
    const words = text.split(' ');
    return words.map((word, wIdx) => (
      <Text key={`w-${wIdx}`} hyphenationCallback={noBreakHyphenation}>
        {word}{wIdx < words.length - 1 ? ' ' : ''}
      </Text>
    ));
  };

  // Helper to render an individual instruction item (wrap={false} ensures each bullet point is never split across pages)
  const renderInstructionRow = (inst, index) => (
    <View key={`instruction-${index}`} style={{ flexDirection: 'row', marginBottom: 5 }} wrap={false}>
      <View style={{ width: 15, alignItems: 'flex-start', flexShrink: 0 }}>
        <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 11, fontFamily: getFontFamily(selectedLanguage) }}>{'\u2022'}</Text>
      </View>
      <View style={{ flex: 1, paddingRight: isIndic ? 6 : 0 }}>
        <Text
          hyphenationCallback={noBreakHyphenation}
          style={{ fontSize: 11, lineHeight: isIndic ? 1.6 : 1.4, fontFamily: getFontFamily(selectedLanguage) }}
        >
          {renderTextWords(inst)}
        </Text>
      </View>
    </View>
  );

  // Helper to render an individual food row (wrap={false} ensures each row of 3 foods is never split across pages)
  const renderFoodRow = (row, rowIdx) => (
    <View
      key={`food-row-${rowIdx}`}
      style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4, width: '100%' }}
      wrap={false}
    >
      {row.map((food, colIdx) => (
        <View key={`food-${rowIdx}-${colIdx}`} style={{ width: '32%', flexDirection: 'row' }}>
          <View style={{ width: 15, alignItems: 'flex-start' }}>
            <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 11, fontFamily: getFontFamily(selectedLanguage) }}>{'\u2022'}</Text>
          </View>
          <View style={{ width: 140 }}>
            <Text
              hyphenationCallback={noBreakHyphenation}
              style={{ fontSize: 11, lineHeight: isIndic ? 1.4 : 1.3, fontFamily: getFontFamily(selectedLanguage) }}
            >
              {renderTextWords(food)}
            </Text>
          </View>
        </View>
      ))}
      {row.length === 1 && <View style={{ width: '66%' }} />}
      {row.length === 2 && <View style={{ width: '33%' }} />}
    </View>
  );

  return (
    <Document>
      {/* First Page: Allergy Images */}
      <Page size="A4" style={styles.page}>
        {PDFHeader}
        {data?.patient?.basicInfo?.name && (
          <View style={{ marginBottom: 20 }}>
            <Text hyphenationCallback={noBreakHyphenation} style={styles.sensitivityLine}>
              {data.patient.basicInfo.name} is sensitive to following elements.
            </Text>
          </View>
        )}
        {allergyImages.length > 0 && (
          <View style={styles.imageGrid}>
            {isSingleImage ? (
              <View style={styles.imageRow}>
                <View key={allergyImages[0].id} style={styles.singleImageContainer}>
                  {allergyImages[0].imageUrl && <Image style={styles.singleAllergyImage} src={allergyImages[0].imageUrl} />}
                  {/* Allergy name in English */}
                  <Text hyphenationCallback={noBreakHyphenation} style={styles.allergyName}>{allergyImages[0].name}</Text>
                </View>
              </View>
            ) : (
              imageRows.map((row, rowIndex) => (
                <View key={rowIndex} style={styles.imageRow}>
                  {row.map((img, colIndex) => (
                    <View key={img.id} style={styles.imageContainer}>
                      {img.imageUrl && <Image style={styles.allergyImage} src={img.imageUrl} />}
                      {/* Allergy name in English */}
                      <Text hyphenationCallback={noBreakHyphenation} style={styles.allergyName}>{img.name}</Text>
                    </View>
                  ))}
                  {row.length === 1 && <View style={styles.imageContainer} />}
                </View>
              ))
            )}
          </View>
        )}
      </Page>

      {/* Second Page: Patient Photo and Instructions */}
      <Page size="A4" style={styles.page}>
        {PDFHeader}
        {data?.patient?.basicInfo?.name && (
          <View style={{ width: '100%', alignItems: 'center', marginBottom: 20 }} wrap={false}>
            {/* Patient image should be passed as a base64 data URL in data.patient.photoUrl */}
            {data?.patient?.photoUrl ? (
              <Image
                style={{ width: 150, height: 150, border: '1px solid #ddd', borderRadius: 2, objectFit: 'cover' }}
                src={data.patient.photoUrl}
              />
            ) : (
            <View style={{ width: 150, height: 150, border: '1px solid #ddd', borderRadius: 2, justifyContent: 'center', alignItems: 'center' }}>
                <Text hyphenationCallback={noBreakHyphenation} style={{ fontFamily: 'Noto Sans Gujarati' }}>No Patient Image</Text>
            </View>
            )}
            <Text hyphenationCallback={noBreakHyphenation} style={{ fontFamily: 'Noto Sans Gujarati', marginTop: 10, fontSize: 16, fontWeight: 'bold' }}>
              {data.patient.basicInfo.name.split(' ')[0]}
            </Text>
          </View>
        )}
        <View style={{ flexGrow: 1 }}>
          {instructions.length > 0 && (
            <View style={{ marginBottom: 15 }}>
              {/* Keep heading together with at least the first instruction so heading is never left alone at bottom of page */}
              <View wrap={false}>
                <Text hyphenationCallback={noBreakHyphenation} style={styles.subHeader}>Instructions For The Patient To Follow</Text>
                {renderInstructionRow(instructions[0], 0)}
              </View>
              {instructions.slice(1).map((inst, i) => renderInstructionRow(inst, i + 1))}
            </View>
          )}
          {foodRows.length > 0 && (
            <View style={{ marginBottom: 10 }}>
              {/* Keep heading together with at least the first food row so heading is never left alone at bottom of page */}
              <View wrap={false}>
                <Text hyphenationCallback={noBreakHyphenation} style={styles.subHeader}>Following Food Items Should Be Avoided</Text>
                {renderFoodRow(foodRows[0], 0)}
              </View>
              {foodRows.slice(1).map((row, rowIdx) => renderFoodRow(row, rowIdx + 1))}
            </View>
          )}
        </View>

        {/* Signature Section - Positioned after last instruction/food with sign space and wrap={false} to handle page end */}
        <View
          style={{
            marginTop: 25,
            width: '100%',
            paddingHorizontal: 5,
          }}
          wrap={false}
        >
          {/* Dedicated space for physical signature / stamp */}
          <View style={{ height: 45 }} />

          {/* Doctor Names Row (Dr. Eshita Shah on Left, Dr. Vipul Shah on Right) */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
            {/* Dr. Eshita Shah - Left Side */}
            <View style={{ width: '50%', alignItems: 'flex-start' }}>
              <Text hyphenationCallback={noBreakHyphenation} style={{ fontWeight: 'bold', fontSize: 12, color: '#000000' }}>
                DR. ESHITA SHAH
              </Text>
            </View>

            {/* Dr. Vipul Shah - Right Side */}
            <View style={{ width: '50%', alignItems: 'flex-end' }}>
              <Text hyphenationCallback={noBreakHyphenation} style={{ fontWeight: 'bold', fontSize: 12, color: '#000000', textAlign: 'right' }}>
                DR. VIPUL SHAH
              </Text>
            </View>
          </View>

          {/* Patient Query Helpline */}
          <View style={{ marginTop: 14, borderTop: '1px solid #ddd', paddingTop: 8, alignItems: 'center' }}>
            <Text hyphenationCallback={noBreakHyphenation} style={{ fontSize: 10, fontWeight: 'bold', color: '#8B0000', textAlign: 'center' }}>
              For any queries, please contact: +91 93166 33039
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InstructionReport;